import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, Button, Input, Spinner, MessageBar, MessageBarBody, Text, Badge, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Field, Switch, } from "@fluentui/react-components";
import { DeleteRegular, ArrowSyncRegular, AddRegular, EditRegular, DismissRegular } from "@fluentui/react-icons";
import { useAuth } from "../../../context/AuthContext";
import { getAllExceptions, createException, updateException, deleteException } from "../../../services/dataService";
import { PeoplePicker } from "../../common/PeoplePicker";
const emptyForm = {
    salarie: "", salarieName: "",
    approbateur1: "", approbateur1Name: "",
    approbateur2: "", approbateur2Name: "",
    motif: "", actif: true,
};
export const ExceptionsView = () => {
    const { apiToken, graphToken } = useAuth();
    const accessToken = apiToken;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const fetchData = useCallback(async () => {
        if (!apiToken)
            return;
        setLoading(true);
        try {
            setItems(await getAllExceptions(apiToken));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Erreur");
        }
        finally {
            setLoading(false);
        }
    }, [apiToken]);
    useEffect(() => { fetchData(); }, [fetchData]);
    const showMessage = (msg, intent) => {
        if (intent === "success") {
            setSuccess(msg);
            setError(null);
        }
        else {
            setError(msg);
            setSuccess(null);
        }
        setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    };
    const openCreate = () => {
        setEditingItem(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };
    const openEdit = (item) => {
        setEditingItem(item);
        setForm({
            salarie: item.salarie,
            salarieName: item.salarie,
            approbateur1: item.approbateur1,
            approbateur1Name: item.approbateur1,
            approbateur2: item.approbateur2,
            approbateur2Name: item.approbateur2,
            motif: item.motif,
            actif: item.actif,
        });
        setDialogOpen(true);
    };
    const handleSave = async () => {
        if (!apiToken || !form.salarie)
            return;
        setSaving(true);
        try {
            const fields = {
                Salarie: form.salarie,
                Approbateur1: form.approbateur1,
                Approbateur2: form.approbateur2,
                Motif: form.motif,
                Actif: form.actif,
            };
            if (editingItem) {
                await updateException(apiToken, editingItem.id, fields);
                setItems((prev) => prev.map((e) => e.id === editingItem.id ? {
                    ...e, salarie: form.salarie, approbateur1: form.approbateur1,
                    approbateur2: form.approbateur2, motif: form.motif, actif: form.actif,
                } : e));
                showMessage("Exception modifiée", "success");
            }
            else {
                const id = await createException(apiToken, fields);
                setItems((prev) => [...prev, {
                        id, salarie: form.salarie, approbateur1: form.approbateur1,
                        approbateur2: form.approbateur2, motif: form.motif, actif: form.actif,
                    }]);
                showMessage("Exception ajoutée", "success");
            }
            setDialogOpen(false);
        }
        catch {
            showMessage("Erreur sauvegarde", "error");
        }
        finally {
            setSaving(false);
        }
    };
    const handleDelete = useCallback(async (id) => {
        if (!apiToken || !confirm("Supprimer cette exception ?"))
            return;
        try {
            await deleteException(apiToken, id);
            setItems((prev) => prev.filter((e) => e.id !== id));
            showMessage("Exception supprimée", "success");
        }
        catch {
            showMessage("Erreur suppression", "error");
        }
    }, [apiToken]);
    if (loading)
        return _jsx(Spinner, { label: "Chargement..." });
    return (_jsxs("div", { children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }, children: [_jsx("h1", { children: "Exceptions d'approbation" }), _jsxs("div", { style: { display: "flex", gap: "8px" }, children: [_jsx(Button, { icon: _jsx(AddRegular, {}), appearance: "primary", onClick: openCreate, children: "Ajouter" }), _jsx(Button, { icon: _jsx(ArrowSyncRegular, {}), onClick: fetchData, appearance: "subtle", children: "Actualiser" })] })] }), error && _jsx(MessageBar, { intent: "error", style: { marginBottom: "16px" }, children: _jsx(MessageBarBody, { children: error }) }), success && _jsx(MessageBar, { intent: "success", style: { marginBottom: "16px" }, children: _jsx(MessageBarBody, { children: success }) }), _jsxs(Table, { "aria-label": "Exceptions", children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHeaderCell, { children: "Salari\u00E9" }), _jsx(TableHeaderCell, { children: "Approbateur N+1" }), _jsx(TableHeaderCell, { children: "Approbateur N+2" }), _jsx(TableHeaderCell, { children: "Motif" }), _jsx(TableHeaderCell, { children: "Actif" }), _jsx(TableHeaderCell, { children: "Actions" })] }) }), _jsxs(TableBody, { children: [items.map((e) => (_jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(Text, { weight: "semibold", children: e.salarie || "-" }) }), _jsx(TableCell, { children: e.approbateur1 || "-" }), _jsx(TableCell, { children: e.approbateur2 || "-" }), _jsx(TableCell, { children: e.motif || "-" }), _jsx(TableCell, { children: _jsx(Badge, { color: e.actif ? "success" : "danger", children: e.actif ? "Oui" : "Non" }) }), _jsx(TableCell, { children: _jsxs("div", { style: { display: "flex", gap: "4px" }, children: [_jsx(Button, { icon: _jsx(EditRegular, {}), appearance: "subtle", size: "small", onClick: () => openEdit(e) }), _jsx(Button, { icon: _jsx(DeleteRegular, {}), appearance: "subtle", size: "small", onClick: () => handleDelete(e.id), style: { color: "#ef4444" } })] }) })] }, e.id))), items.length === 0 && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 6, style: { textAlign: "center", padding: "24px" }, children: "Aucune exception." }) }))] })] }), _jsx(Dialog, { open: dialogOpen, onOpenChange: (_, data) => setDialogOpen(data.open), children: _jsx(DialogSurface, { style: { maxWidth: "520px" }, children: _jsxs(DialogBody, { children: [_jsx(DialogTitle, { action: _jsx(Button, { appearance: "subtle", icon: _jsx(DismissRegular, {}), onClick: () => setDialogOpen(false) }), children: editingItem ? "Modifier l'exception" : "Ajouter une exception" }), _jsxs(DialogContent, { style: { display: "flex", flexDirection: "column", gap: "16px", paddingTop: "16px" }, children: [_jsx(PeoplePicker, { label: "Salari\u00E9", required: true, selectedName: form.salarieName, selectedEmail: form.salarie, onSelect: (user) => setForm((prev) => ({ ...prev, salarie: user.mail, salarieName: user.displayName })), onClear: () => setForm((prev) => ({ ...prev, salarie: "", salarieName: "" })), accessToken: graphToken || accessToken || "", placeholder: "Rechercher le salari\u00E9..." }), _jsx(PeoplePicker, { label: "Approbateur N+1", selectedName: form.approbateur1Name, selectedEmail: form.approbateur1, onSelect: (user) => setForm((prev) => ({ ...prev, approbateur1: user.mail, approbateur1Name: user.displayName })), onClear: () => setForm((prev) => ({ ...prev, approbateur1: "", approbateur1Name: "" })), accessToken: graphToken || accessToken || "", placeholder: "Rechercher le N+1..." }), _jsx(PeoplePicker, { label: "Approbateur N+2", selectedName: form.approbateur2Name, selectedEmail: form.approbateur2, onSelect: (user) => setForm((prev) => ({ ...prev, approbateur2: user.mail, approbateur2Name: user.displayName })), onClear: () => setForm((prev) => ({ ...prev, approbateur2: "", approbateur2Name: "" })), accessToken: graphToken || accessToken || "", placeholder: "Rechercher le N+2..." }), _jsx(Field, { label: "Motif", children: _jsx(Input, { value: form.motif, onChange: (_, d) => setForm({ ...form, motif: d.value }), placeholder: "Raison de l'exception" }) }), _jsx(Field, { label: "Actif", children: _jsx(Switch, { checked: form.actif, onChange: (_, d) => setForm({ ...form, actif: d.checked }) }) })] }), _jsxs(DialogActions, { children: [_jsx(Button, { appearance: "secondary", onClick: () => setDialogOpen(false), children: "Annuler" }), _jsx(Button, { appearance: "primary", onClick: handleSave, disabled: saving || !form.salarie, children: saving ? "Enregistrement..." : editingItem ? "Modifier" : "Ajouter" })] })] }) }) })] }));
};
